import React, { useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Checkbox,
  Typography,
  Chip,
  IconButton,
  Button,
  TablePagination,
  useTheme,
  alpha,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
  Schedule as ScheduleIcon,
  Comment as CommentIcon,
  CommentOutlined as CommentOutlinedIcon,
} from '@mui/icons-material';
import { formatDateSafe } from '../../utils/dateUtils';
import { Invoice } from '../../types/invoice';
import PDFDownload from '../PDF/PDFDownload';
import czechTranslations from '../../translations/czech';
import {
  OptimizedStatusCell,
  OptimizedAmountCell,
  OptimizedDateCell,
  OptimizedBusinessUnitCell,
  OptimizedActionsCell,
  OptimizedInvoiceNumberCell,
  OptimizedSupplierCell,
  OptimizedCustomerCell,
  OptimizedPDFDownload,
  OptimizedVATDateCell,
} from './OptimizedComponents';

const statusColors = {
  new: 'default',
  pending: 'warning',
  reviewed: 'info',
  ready_for_payment: 'primary',
  sent_to_bank: 'secondary',
  paid: 'success',
  paid_in_cash: 'success',
  rejected: 'error'
} as const;

const aboStatusLabels = {
  generated: 'Vytvořeno',
  sent: 'Odesláno',
  sent_to_fio: 'Odesláno do FIO',
  error: 'Chyba'
} as const;

// Use translations for status labels
const getStatusLabel = (status: string) => {
  return czechTranslations.invoices.status[status as keyof typeof czechTranslations.invoices.status] || status;
};

interface OptimizedInvoiceTableProps {
  data: Invoice[];
  loading: boolean;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  selectedInvoices: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  onPaginationChange: (page: number, pageSize: number) => void;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (invoice: Invoice) => void;
  onAssignCompany: (invoice: Invoice) => void;
  onSortChange?: (updater: any) => void;
  onFilterChange?: (updater: any) => void;
  onOpenNotes?: (invoice: Invoice) => void;
}

// Memoized cell components are now imported from OptimizedComponents.tsx

// Mobile Card Component - Optimized for Safari
const MobileInvoiceCard = React.memo(({
  invoice,
  selected,
  onSelect,
  onView,
  onEdit,
  onDelete,
  onAssignCompany,
  showSelection,
}: {
  invoice: Invoice;
  selected: boolean;
  onSelect: (id: number, selected: boolean) => void;
  onView: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (invoice: Invoice) => void;
  onAssignCompany: (invoice: Invoice) => void;
  showSelection: boolean;
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCardClick = useCallback((event: React.MouseEvent) => {
    if (event.target instanceof HTMLElement) {
      if (
        event.target.closest('.MuiIconButton-root') ||
        event.target.closest('.MuiCheckbox-root') ||
        event.target.closest('.MuiButton-root')
      ) {
        return;
      }
    }
    
    if (showSelection && onSelect) {
      onSelect(invoice.id, !selected);
    } else {
      onView(invoice.id);
    }
  }, [showSelection, onSelect, selected, invoice.id, onView]);

  return (
    <Card
      className="virtual-scroll-item"
      sx={{
        mb: 2,
        cursor: 'pointer',
        transition: 'all 0.1s ease-in-out', // Reduced transition time for Safari
        border: selected ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(0, 0, 0, 0.05)',
        backgroundColor: selected ? alpha(theme.palette.primary.main, 0.04) : 'background.paper',
        '&:hover': {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)', // Simplified shadow for Safari
          transform: 'translateY(-1px)', // Reduced transform for Safari
        },
      }}
      onClick={handleCardClick}
    >
      <CardContent sx={{ p: 2 }}>
        {/* Header Row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          {showSelection && (
            <Checkbox
              checked={selected}
              onChange={(e) => onSelect(invoice.id, e.target.checked)}
              sx={{ mr: 1, mt: -0.5 }}
              onClick={(e) => e.stopPropagation()}
            />
          )}
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            {/* Invoice Number and Status */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {invoice.invoiceNumber}
              </Typography>
              <OptimizedStatusCell status={invoice.status} />
            </Box>
          </Box>
          
          <IconButton
            onClick={handleMenuOpen}
            sx={{ ml: 1 }}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Company Information */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                wordBreak: 'break-word',
                maxWidth: '100%',
              }}
            >
              {invoice.supplierName} → {invoice.customerName}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <OptimizedDateCell date={invoice.invoiceDate} />
          </Box>
        </Stack>

        {/* Amount and Due Date */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <OptimizedAmountCell amount={invoice.totalAmount} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Splatnost: <OptimizedDateCell date={invoice.dueDate} />
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(invoice.pdfData || invoice.pdfFilename) && (
              <PDFDownload
                invoiceId={invoice.id}
                invoiceNumber={invoice.invoiceNumber}
                hasPdf={true}
                size="small"
              />
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
});

const OptimizedInvoiceTable: React.FC<OptimizedInvoiceTableProps> = ({
  data,
  loading,
  pagination,
  selectedInvoices,
  onSelectionChange,
  onPaginationChange,
  onView,
  onEdit,
  onDelete,
  onAssignCompany,
  onSortChange,
  onFilterChange,
  onOpenNotes,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const parentRef = useRef<HTMLDivElement>(null);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Handle column sorting
  const handleSort = useCallback((columnKey: string) => {
    let newDirection: 'asc' | 'desc' = 'asc';
    
    if (sortField === columnKey) {
      // Same column clicked - cycle through: asc -> desc -> default (no sort)
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else {
        // Reset to default (no sorting)
        setSortField('');
        setSortDirection('asc');
        if (onSortChange) {
          onSortChange({
            field: '',
            direction: 'asc'
          });
        }
        return;
      }
    } else {
      // New column clicked - start with ascending
      newDirection = 'asc';
    }
    
    setSortField(columnKey);
    setSortDirection(newDirection);
    
    // Call parent sort handler if provided
    if (onSortChange) {
      onSortChange({
        field: columnKey,
        direction: newDirection
      });
    }
  }, [sortField, sortDirection, onSortChange]);

  // Memoized columns for desktop table with optimized components
  const columns = useMemo(() => [
    {
      key: 'select',
      label: 'Vybrat',
      width: 80,
      sortable: false,
      render: (invoice: Invoice, selected: boolean) => (
        <Checkbox
          checked={selected}
          onChange={(e) => {
            if (e.target.checked) {
              onSelectionChange([...selectedInvoices, invoice.id]);
            } else {
              onSelectionChange(selectedInvoices.filter(id => id !== invoice.id));
            }
          }}
          size="small"
          sx={{ padding: '4px' }}
        />
      )
    },
    {
      key: 'invoiceNumber',
      label: 'Číslo faktury',
      width: 140,
      sortable: true,
      render: (invoice: Invoice) => <OptimizedInvoiceNumberCell invoiceNumber={invoice.invoiceNumber} />
    },
    {
      key: 'invoiceDate',
      label: 'Datum vystavení',
      width: 130,
      sortable: true,
      render: (invoice: Invoice) => <OptimizedDateCell date={invoice.invoiceDate} />
    },
    {
      key: 'dueDate',
      label: 'Datum splatnosti',
      width: 130,
      sortable: true,
      render: (invoice: Invoice) => <OptimizedDateCell date={invoice.dueDate} />
    },
    {
      key: 'vatRenderedDate',
      label: 'Datum vyúčtování DPH',
      width: 160,
      sortable: true,
      render: (invoice: Invoice) => <OptimizedVATDateCell date={invoice.vatRenderedDate || null} />
    },
    {
      key: 'supplierName',
      label: 'Dodavatel',
      width: 250,
      sortable: true,
      render: (invoice: Invoice) => <OptimizedSupplierCell invoice={invoice} />
    },
    {
      key: 'businessUnit',
      label: 'Účetní jednotka',
      width: 180,
      sortable: false,
      render: (invoice: Invoice) => <OptimizedBusinessUnitCell businessUnit={invoice.businessUnit} />
    },
    {
      key: 'customerName',
      label: 'Odběratel',
      width: 250,
      sortable: true,
      render: (invoice: Invoice) => <OptimizedCustomerCell invoice={invoice} />
    },
    {
      key: 'totalAmount',
      label: 'Částka',
      width: 120,
      sortable: true,
      render: (invoice: Invoice) => <OptimizedAmountCell amount={invoice.totalAmount} />
    },
    {
      key: 'status',
      label: 'Zpracování',
      width: 120,
      sortable: true,
      render: (invoice: Invoice) => <OptimizedStatusCell status={invoice.status} />
    },
    {
      key: 'pdf',
      label: 'PDF',
      width: 80,
      sortable: false,
      render: (invoice: Invoice) => <OptimizedPDFDownload invoice={invoice} />
    },
    {
      key: 'internalNotes',
      label: '💬',
      width: 60,
      sortable: false,
      render: (invoice: Invoice) => {
        const hasNote = invoice.internalNotes && invoice.internalNotes.trim().length > 0;
        const notePreview = hasNote ? 
          (invoice.internalNotes!.length > 50 ? 
            invoice.internalNotes!.substring(0, 50) + '...' : 
            invoice.internalNotes!) : 
          czechTranslations.invoices.internalNotes.addTooltip;
        
        return (
          <Tooltip title={notePreview}>
            <IconButton
              size="small"
              onClick={() => onOpenNotes?.(invoice)}
              color={hasNote ? "primary" : "default"}
              sx={{ padding: '4px' }}
            >
              {hasNote ? <CommentIcon fontSize="small" /> : <CommentOutlinedIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        );
      }
    },
    {
      key: 'actions',
      label: 'Akce',
      width: 160,
      sortable: false,
      render: (invoice: Invoice) => (
        <OptimizedActionsCell
          invoice={invoice}
          onView={onView}
          onEdit={onEdit}
          onDelete={onDelete}
          onAssignCompany={onAssignCompany}
        />
      )
    }
  ], [selectedInvoices, onSelectionChange, onView, onEdit, onDelete, onAssignCompany, onOpenNotes]);

  // Calculate total table width for better alignment
  const totalWidth = useMemo(() => {
    return columns.reduce((sum, col) => sum + col.width, 0);
  }, [columns]);

  // Sort data based on current sort configuration
  const sortedData = useMemo(() => {
    // If no sort field is selected, return data in original order (default)
    if (!sortField) return data;

    const sorted = [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      // Get the values to compare based on sort field
      switch (sortField) {
        case 'invoiceNumber':
          aValue = a.invoiceNumber || '';
          bValue = b.invoiceNumber || '';
          break;
        case 'invoiceDate':
          aValue = new Date(a.invoiceDate || '');
          bValue = new Date(b.invoiceDate || '');
          // Handle invalid dates
          if (isNaN(aValue.getTime())) aValue = new Date(0);
          if (isNaN(bValue.getTime())) bValue = new Date(0);
          break;
        case 'dueDate':
          aValue = new Date(a.dueDate || '');
          bValue = new Date(b.dueDate || '');
          // Handle invalid dates
          if (isNaN(aValue.getTime())) aValue = new Date(0);
          if (isNaN(bValue.getTime())) bValue = new Date(0);
          break;
        case 'vatRenderedDate':
          aValue = new Date(a.vatRenderedDate || '');
          bValue = new Date(b.vatRenderedDate || '');
          // Handle invalid dates
          if (isNaN(aValue.getTime())) aValue = new Date(0);
          if (isNaN(bValue.getTime())) bValue = new Date(0);
          break;
        case 'supplierName':
          aValue = (a.supplierName || '').toLowerCase();
          bValue = (b.supplierName || '').toLowerCase();
          break;
        case 'customerName':
          aValue = (a.customerName || '').toLowerCase();
          bValue = (b.customerName || '').toLowerCase();
          break;
        case 'totalAmount':
          aValue = typeof a.totalAmount === 'number' ? a.totalAmount : 0;
          bValue = typeof b.totalAmount === 'number' ? b.totalAmount : 0;
          break;
        case 'status':
          // Sort by translated Czech labels instead of raw status values
          aValue = getStatusLabel(a.status || '').toLowerCase();
          bValue = getStatusLabel(b.status || '').toLowerCase();
          break;
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Compare values
      let comparison = 0;
      if (aValue > bValue) {
        comparison = 1;
      } else if (aValue < bValue) {
        comparison = -1;
      }

      // Apply sort direction
      return sortDirection === 'desc' ? comparison * -1 : comparison;
    });
    
    return sorted;
  }, [data, sortField, sortDirection]);

  // Note: Virtual scrolling temporarily disabled for better alignment
  // const virtualizer = useVirtualizer({
  //   count: sortedData.length,
  //   getScrollElement: () => parentRef.current,
  //   estimateSize: () => 60, // Estimated row height
  //   overscan: 5, // Render 5 extra items for smooth scrolling
  // });

  // Mobile layout with virtual scrolling
  if (isMobile) {
    return (
      <Box 
        className="virtual-scroll-container invoice-list-container"
        sx={{ 
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <Typography>Načítání...</Typography>
          </Box>
        ) : sortedData.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Žádné faktury nenalezeny
            </Typography>
          </Paper>
        ) : (
          <>
            {/* Mobile Selection Header */}
            {selectedInvoices.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: 2,
                p: 2,
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: 2
              }}>
                <Typography variant="body2">
                  Vybráno: {selectedInvoices.length}
                </Typography>
                <IconButton
                  color="inherit"
                  onClick={() => {
                    // Handle bulk delete
                    console.log('Bulk delete selected invoices');
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            
            {/* Mobile Cards */}
            <Box
              sx={{
                maxHeight: '60vh',
                overflow: 'auto',
              }}
            >
              {sortedData.map((invoice) => (
                <Box
                  key={invoice.id}
                  sx={{ mb: 1 }}
                >
                  <MobileInvoiceCard
                    invoice={invoice}
                    selected={selectedInvoices.includes(invoice.id)}
                    onSelect={(id, selected) => {
                      if (selected) {
                        onSelectionChange([...selectedInvoices, id]);
                      } else {
                        onSelectionChange(selectedInvoices.filter(invoiceId => invoiceId !== id));
                      }
                    }}
                    onView={onView}
                    onEdit={onEdit}
                    onDelete={onDelete}
                    onAssignCompany={onAssignCompany}
                    showSelection={selectedInvoices.length > 0}
                  />
                </Box>
              ))}
            </Box>
            
            {/* Mobile Pagination */}
            {pagination.total > pagination.limit && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <TablePagination
                  component="div"
                  count={pagination.total}
                  page={pagination.page - 1}
                  onPageChange={(event, newPage) => {
                    onPaginationChange(newPage + 1, pagination.limit);
                  }}
                  rowsPerPage={pagination.limit}
                  onRowsPerPageChange={(event) => {
                    const newPageSize = parseInt(event.target.value, 10);
                    onPaginationChange(1, newPageSize);
                  }}
                  rowsPerPageOptions={[25, 50, 100]}
                  labelRowsPerPage="Řádků na stránku:"
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} z ${count !== -1 ? count : `více než ${to}`}`
                  }
                />
              </Box>
            )}
          </>
        )}
      </Box>
    );
  }

  // Desktop layout with virtual scrolling
  return (
    <Paper className="optimized-table invoice-table-container" sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Sorting Controls */}
      {sortField && (
        <Box sx={{ 
          p: 1, 
          backgroundColor: 'grey.50', 
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="body2" color="text.secondary">
            Seřazeno podle: <strong>{columns.find(col => col.key === sortField)?.label}</strong> ({sortDirection === 'asc' ? 'vzestupně' : 'sestupně'})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleSort(sortField)} // This will reset to default
            title="Resetovat na výchozí řazení"
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Resetovat řazení
          </Button>
        </Box>
      )}
      <TableContainer
        ref={parentRef}
        sx={{
          height: '70vh',
          overflow: 'auto',
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <Table stickyHeader sx={{ minWidth: totalWidth, tableLayout: 'fixed' }}>
          <colgroup>
            {columns.map((column, index) => (
              <col key={index} style={{ width: `${column.width}px` }} />
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={`${column.key}-header-${index}`}
                  data-sortable={column.sortable}
                  sx={{
                    backgroundColor: 'background.paper',
                    fontWeight: 'bold',
                    padding: '8px 12px',
                    cursor: column.sortable ? 'pointer' : 'default',
                    '&:hover': column.sortable ? {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    } : {},
                  }}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 'bold',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={column.label}
                  >
                    {column.label}
                    {column.sortable && (
                      <TableSortLabel
                        active={sortField === column.key}
                        direction={sortField === column.key ? sortDirection : 'asc'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort(column.key);
                        }}
                        sx={{ 
                          ml: 0.5, 
                          display: 'inline-block',
                          opacity: sortField === column.key ? 1 : 0.3
                        }}
                      />
                    )}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>Načítání...</Typography>
                </TableCell>
              </TableRow>
            ) : sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Žádné faktury nenalezeny
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedData.map((invoice, index) => {
                const isSelected = selectedInvoices.includes(invoice.id);
                
                return (
                  <TableRow
                    key={invoice.id}
                    hover
                    sx={{
                      backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={`${column.key}-${colIndex}`}
                        sx={{
                          padding: '8px 12px',
                          verticalAlign: 'top',
                          overflow: 'hidden',
                        }}
                      >
                        {column.render(invoice, isSelected)}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Pagination */}
      <TablePagination
        component="div"
        count={pagination.total}
        page={pagination.page - 1}
        onPageChange={(event, newPage) => {
          onPaginationChange(newPage + 1, pagination.limit);
        }}
        rowsPerPage={pagination.limit}
        onRowsPerPageChange={(event) => {
          const newPageSize = parseInt(event.target.value, 10);
          onPaginationChange(1, newPageSize);
        }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="Řádků na stránku:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} z ${count !== -1 ? count : `více než ${to}`}`
        }
      />
    </Paper>
  );
};

export default OptimizedInvoiceTable;
